/* Shopify remains the source of truth. No prices, IDs, or inventory are stored here. */
(() => {
  'use strict';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const controllers = new WeakMap();
  function mount(section) {
    if (controllers.has(section)) return;
    const lifecycle = new AbortController();
    controllers.set(section, lifecycle);
    const on = (element, event, handler) => element?.addEventListener(event, handler, { signal: lifecycle.signal });
    const form = section.querySelector('.product-form');
    const select = form?.querySelector('[data-variant-select]');
    if (!select) return;
    const quantity = form.querySelector('[name="quantity"]');
    const buttons = section.querySelector('[data-variant-buttons]');
    const addButtons = [...section.querySelectorAll('[data-add]')];
    const error = section.querySelector('[data-form-error]');
    const gallery = section.querySelector('[data-gallery]');
    const slides = [...section.querySelectorAll('[data-media-id]')];
    const thumbLinks = [...section.querySelectorAll('[data-media-target]')];
    let busy = false;

    function markMedia(id) {
      thumbLinks.forEach(link => link.setAttribute('aria-current', String(link.dataset.mediaTarget === id)));
    }
    function showMedia(id, instant = false) {
      const target = slides.find(slide => slide.dataset.mediaId === id);
      if (!target || !gallery) return;
      const left = target.getBoundingClientRect().left - gallery.getBoundingClientRect().left + gallery.scrollLeft;
      gallery.scrollTo({ left, behavior: instant || reduceMotion() ? 'instant' : 'smooth' });
      markMedia(id);
    }
    function updateVariant({ updateURL = false, instant = false } = {}) {
      const option = select.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === 'true';
      section.querySelectorAll('[data-price]:not(option)').forEach(node => { node.textContent = option.dataset.price; });
      const compare = section.querySelector('[data-compare]');
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
      buttons.querySelectorAll('button').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.variantId === option.value)); });
      addButtons.forEach(button => {
        button.disabled = busy || !available;
        button.textContent = busy ? section.dataset.addingLabel : available ? section.dataset.addLabel : section.dataset.soldLabel;
      });
      section.querySelector('[data-availability]').textContent = available ? '' : section.dataset.soldLabel;
      const checkout = section.querySelector('[data-dynamic]');
      if (checkout) checkout.hidden = !available || busy;
      const min = Number(option.dataset.min || 1);
      const step = Number(option.dataset.step || 1);
      quantity.min = String(min);
      quantity.step = String(step);
      if (option.dataset.max) quantity.max = option.dataset.max;
      else quantity.removeAttribute('max');
      let value = Math.max(min, Number(quantity.value) || min);
      value = min + Math.ceil((value - min) / step) * step;
      if (option.dataset.max) value = Math.min(value, Number(option.dataset.max));
      quantity.value = String(value);
      if (option.dataset.media) showMedia(option.dataset.media, instant);
      if (updateURL && section.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        url.searchParams.delete('option_values');
        window.history.replaceState({}, '', url);
      }
    }

    // Progressive enhancement: the native select remains usable without JavaScript.
    if (select.options.length <= 12 && !select.closest('.variant-field').hidden) {
      const colors = { green: '#9cbb8e', blue: '#9bbacb', pink: '#e8b6c7' };
      [...select.options].forEach(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'variant-button';
        button.dataset.variantId = option.value;
        button.dataset.available = option.dataset.available;
        button.setAttribute('aria-pressed', String(option.selected));
        button.setAttribute('aria-label', option.dataset.title + (option.dataset.available === 'false' ? ' — ' + section.dataset.soldLabel : ''));
        const color = colors[option.dataset.title.toLowerCase()];
        if (color) {
          const dot = document.createElement('span');
          dot.className = 'variant-dot';
          dot.style.backgroundColor = color;
          dot.setAttribute('aria-hidden', 'true');
          button.append(dot);
        }
        button.append(document.createTextNode(option.dataset.title));
        on(button, 'click', () => {
          if (busy) return;
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        buttons.append(button);
      });
      buttons.hidden = false;
      select.hidden = true;
      const label = section.querySelector(`label[for="${select.id}"]`);
      if (label) {
        label.removeAttribute('for');
        label.id = `${select.id}-label`;
        buttons.setAttribute('aria-labelledby', label.id);
      }
    }
    on(select, 'change', () => {
      error.hidden = true;
      updateVariant({ updateURL: true });
    });
    thumbLinks.forEach(link => on(link, 'click', event => {
      event.preventDefault();
      showMedia(link.dataset.mediaTarget);
    }));
    if ('IntersectionObserver' in window && gallery) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) markMedia(entry.target.dataset.mediaId);
          else entry.target.querySelectorAll('video').forEach(video => video.pause());
        });
      }, { root: gallery, threshold: 0.6 });
      slides.forEach(slide => observer.observe(slide));
      lifecycle.signal.addEventListener('abort', () => observer.disconnect());
    }
    const mobile = section.querySelector('[data-mobile-purchase]');
    if (mobile && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        mobile.hidden = entries[0].isIntersecting;
      }, { threshold: 0 });
      observer.observe(form.querySelector('[data-add]'));
      document.body.classList.add('has-mobile-purchase');
      lifecycle.signal.addEventListener('abort', () => observer.disconnect());
    }
    on(form, 'submit', async event => {
      // Shopify accelerated checkout owns its own flow.
      if (event.submitter && event.submitter.name !== 'add') return;
      if (!window.fetch) return;
      event.preventDefault();
      if (busy || select.selectedOptions[0].dataset.available !== 'true' || !form.reportValidity()) return;
      const payload = new FormData(form);
      payload.delete('return_to');
      error.hidden = true;
      busy = true;
      updateVariant();
      form.setAttribute('aria-busy', 'true');
      let confirmedRejection = false;
      try {
        const response = await fetch(section.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: payload });
        const data = await response.json();
        if (!response.ok || data.status) {
          confirmedRejection = response.status >= 400 && response.status < 500;
          throw new Error(typeof data.description === 'string' ? data.description : section.dataset.errorLabel);
        }
        window.location.assign(section.dataset.cartUrl);
      } catch (failure) {
        error.textContent = confirmedRejection ? failure.message : section.dataset.networkLabel;
        const link = document.createElement('a');
        link.href = section.dataset.cartUrl;
        link.textContent = 'View bag';
        error.append(document.createElement('br'), link);
        error.hidden = false;
        busy = false;
        updateVariant();
        form.removeAttribute('aria-busy');
        error.scrollIntoView({ behavior: reduceMotion() ? 'instant' : 'smooth', block: 'center' });
      }
    });
    updateVariant({ instant: true });
    if (window.Shopify?.designMode && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
  }
  function mountAll(root) {
    if (root.matches?.('[data-product-section]')) mount(root);
    root.querySelectorAll('[data-product-section]').forEach(mount);
  }
  mountAll(document);
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-product-section]').forEach(section => {
      controllers.get(section)?.abort();
      controllers.delete(section);
    });
    if (!document.querySelector('[data-mobile-purchase]')) document.body.classList.remove('has-mobile-purchase');
  });
})();
